import { useContext, useState } from "react";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import { EmpresasContext, empresaType } from "../../context/EmpresasContext";
import { Autocomplete, Skeleton, TextField } from "@mui/material";
import { useMutation, useQuery } from "react-query";
import AccessControl, { AccessControlResponse } from "../../services/AccessControl";
import AttToken from "../../services/AttToken";
import { toast } from "react-toastify";
import SaveIcon from "@mui/icons-material/Save";
import queryClient from "../../services/query";
import Checkbox from "@mui/material/Checkbox";

const accessControlService = new AccessControl();
const functionalityNames: Record<string, string> = {
  notificacaoWeb: "Chamados e Notificações",
  relatorios: "Relatórios",
  home: "Home",
  preenchimento: "Preenchimento Logado",
  "plano-de-acao": "Plano de Ação",
  pesquisas: "Pesquisas Legado",
  powerbi: "Power BI",
  configuracoes: "Configurações",
  locais: "Gestão de Locais",
  servicos: "Gestão de Serviços",
  usuarios: "Gestão de Usuários",
  clientes: "Gestão de Clientes",
  equipes: "Gestão de Equipes",
  gerencias: "Gestão de Gerências",
  formularios: "Gestão de Formulários",
  notificacoes: "Gestão de notificações e chamados",
  fluxo: "Fluxo",
};
type simpleObject = {
  id: string;
  nome: string;
};

function not(a: simpleObject[], b: simpleObject[]) {
  return a?.filter((value) => !b?.some(bValue => bValue.id === value.id));
}

function intersection(a: simpleObject[], b: simpleObject[]) {
  return a?.filter((value) => b?.some(bValue => bValue.id === value.id));
}

function union(a: simpleObject[], b: simpleObject[]) {
  return [...a, ...not(b, b.filter(item => !a.some(aValue => aValue.id === item.id)))];
}

export default function SelectAllTransferList() {
  const { empresas, onLoading } = useContext(EmpresasContext);
  const [empresaId, setEmpresaId] = useState<string>("");
  const [checked, setChecked] = useState<simpleObject[]>([]);
  const [left, setLeft] = useState<simpleObject[]>([]);
  const [right, setRight] = useState<simpleObject[]>([]);

  const { isLoading, isFetching } = useQuery({
    queryKey: ["ACESSOS_ADM", empresaId],
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error("Token não encontrado");
      const response = await accessControlService.getAccessByCompany(token, empresaId);
      if (!response) throw new Error("Erro ao buscar acessos da empresa");
      return response;
    }, onSuccess: (data) => {
      if (data) {
        setChecked([]);
        setLeft(toSimpleObjects(data.roleFuncs.filter((id) => !data.admFuncs.includes(id))));
        setRight(toSimpleObjects(data.admFuncs));
      }
    },
    retry: 1,
    refetchOnWindowFocus: false,
    enabled: !!empresaId,
  });

  const { mutate, isLoading: isMutating } = useMutation({
    mutationKey: ["CHANGE_ACESSOS_ADM", empresaId],
    mutationFn: async () => {
      const promise = toast.promise(
        async () => {
          const token = await AttToken();
          if (!token) throw new Error("Token não encontrado");
          const response = await accessControlService.changeAccess(token, {
            empresa: empresaId,
            admFuncs: right.map(({ id }) => id),
            roleFuncs: left.map(({ id }) => id),
          });
          if (!response) throw new Error("Erro ao salvar alterações");
          return response;
        },
        {
          pending: "Salvando alterações...",
          success: "Alterações salvas com sucesso",
          error: "Erro ao salvar alterações",
        },
      );
      return promise;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ACESSOS_ADM", empresaId] });
    },
  });

  const loading = isLoading || isFetching;

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value: simpleObject) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items: simpleObject[]) => intersection(checked, items).length;

  const handleToggleAll = (items: simpleObject[]) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    setRight(right.concat(left.filter(item => leftChecked.includes(item))));
    setLeft(not(left, left.filter(item => leftChecked.includes(item))));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(right.filter(item => rightChecked.includes(item))));
    setRight(not(right, right.filter(item => rightChecked.includes(item))));
    setChecked(not(checked, rightChecked));
  };

  const customList = (title: React.ReactNode, items: simpleObject[]) => (
    <Card sx={{ height: "100%", minHeight: "100%" }}>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        avatar={
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0}
            disabled={items.length === 0}
            slotProps={{
              input: { "aria-label": "all items selected" },
            }}
          />
        }
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} selecionados`}
      />
      <Divider />
      <List
        sx={{
          width: 300,
          height: "100%",
          minHeight: "100%",
          bgcolor: "background.paper",
          overflow: "auto",
        }}
        dense
        component="div"
        role="list">
        {items.map((value) => {
          return (
            <ListItemButton key={`item-${value.id}`} role="listitem" onClick={handleToggle(value)}>
              <ListItemIcon>
                <Checkbox
                  checked={checked.includes(value)}
                  tabIndex={-1}
                  disableRipple
                  slotProps={{
                    input: { "aria-labelledby": value.nome },
                  }}
                />
              </ListItemIcon>
              <ListItemText id={value.id} primary={value.nome} />
            </ListItemButton>
          );
        })}
      </List>
    </Card>
  );

  function Loading() {
    return (
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} width="100%">
        <Skeleton variant="rectangular" width={300} height={118} />
        <Stack direction="column" alignItems="center" justifyContent="center" spacing={2}>
          <Skeleton variant="rectangular" width={80} height={50} />
          <Skeleton variant="rectangular" width={80} height={50} />
        </Stack>
        <Skeleton variant="rectangular" width={300} height={118} />
      </Stack>
    );
  }

  return (
    <Stack direction="column" alignItems="center" justifyContent="flex-start" spacing={2} padding={2} width="100%">
      <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1} width="100%">
        <Autocomplete
          fullWidth
          disablePortal
          loading={onLoading}
          disabled={onLoading}
          getOptionLabel={(option: empresaType) => option.nome}
          isOptionEqualToValue={(option: empresaType, value: empresaType) => option.id === value.id}
          options={
            [...(new Set(empresas) as unknown as  empresaType[])]
              ?.filter((empresa: empresaType) => empresa.status === "ativo")
              ?.sort((a: empresaType, b: empresaType) => a.nome.localeCompare(b.nome))
              ?.map((empresa) => ({ ...empresa })) ?? []
          }
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.nome}
            </li>
          )}
          renderInput={(params) => <TextField {...params} label="Empresa" />}
          onChange={(_eve, value) => {
            setEmpresaId(value?.id ?? "");
            setChecked([]);
            setLeft([]);
            setRight([]);
          }}
        />
        <Button onClick={() => mutate()} disabled={!empresaId || isMutating} endIcon={<SaveIcon />} variant="contained" color="success">
          Salvar
        </Button>
      </Stack>
      {loading ? (
        <Loading />
      ) : (
        empresaId && (
          <>
            <Grid container spacing={2} sx={{ justifyContent: "center", alignItems: "center" }}>
              <Grid>{customList("Acessos dos perfis", left)}</Grid>
              <Stack direction="column" alignItems="center" justifyContent="center" spacing={2}>
                <Button variant="outlined" onClick={handleCheckedRight} disabled={leftChecked.length === 0}>
                  &gt;
                </Button>
                <Button variant="outlined" onClick={handleCheckedLeft} disabled={rightChecked.length === 0}>
                  &lt;
                </Button>
              </Stack>
              <Grid>{customList(`Acessos apenas para "admin"`, right)}</Grid>
            </Grid>
          </>
        )
      )}
    </Stack>
  );
}

function toSimpleObjects(functionalities: AccessControlResponse["admFuncs"]): simpleObject[] {
  return functionalities.map((id) => ({ id, nome: functionalityNames[id] ?? id }));
}
